import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ChevronLeft, ChevronRight, CalendarDays, Loader2 } from "lucide-react";

const ESTADOS_CERRADOS = ["resuelto", "cerrado", "completado"];
const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export default function HallazgosCalendarioTab({ onOpenTicket }) {
  const { getAuthHeaders } = useAuth();
  const [ref, setRef] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const base = getAuthHeaders();
        const opts = { ...base, headers: { ...base.headers, "X-Modulo": "hallazgos_auditoria" } };
        const [resT, resP] = await Promise.all([
          axios.get(`${API}/tickets`, opts),
          axios.get(`${API}/tickets/prioridades`, opts),
        ]);
        const slaPorPrioridad = {};
        (resP.data || []).forEach((p) => { slaPorPrioridad[(p.name || "").toLowerCase()] = p.sla_hours; });
        const abiertos = (resT.data || []).filter((t) => !ESTADOS_CERRADOS.includes(t.estado));
        // Vencimiento: fecha compromiso si existe; si no, creación + SLA de su prioridad
        const conVencimiento = abiertos.map((t) => {
          let venc = t.fecha_compromiso ? String(t.fecha_compromiso).slice(0, 10) : null;
          if (!venc && t.fecha_creacion) {
            const horas = slaPorPrioridad[(t.prioridad || "").toLowerCase()];
            if (horas) {
              const d = new Date(t.fecha_creacion);
              d.setTime(d.getTime() + horas * 3600 * 1000);
              venc = d.toISOString().slice(0, 10);
            }
          }
          return venc ? { ...t, _vencimiento: venc, _esCompromiso: !!t.fecha_compromiso } : null;
        }).filter(Boolean);
        setItems(conVencimiento);
      } catch { /* noop */ }
      finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const y = ref.getFullYear();
  const m = ref.getMonth();
  const hoyStr = new Date().toISOString().slice(0, 10);
  const diasMes = new Date(y, m + 1, 0).getDate();
  const offset = (new Date(y, m, 1).getDay() + 6) % 7;
  const porDia = {};
  items.forEach((t) => {
    const f = t._vencimiento;
    if (f.startsWith(`${y}-${String(m + 1).padStart(2, "0")}`)) {
      (porDia[f] = porDia[f] || []).push(t);
    }
  });
  const totalMes = Object.values(porDia).reduce((a, b) => a + b.length, 0);
  const celdas = [...Array(offset).fill(null), ...Array.from({ length: diasMes }, (_, i) => i + 1)];

  return (
    <div className="p-4 md:p-8" data-testid="calendario-compromisos-tab">
      <div className="max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#0C314C] dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Calendario de Compromisos</h2>
            <Badge variant="outline" data-testid="calendario-total-badge">{totalMes} en el mes</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setRef(new Date(y, m - 1, 1))} data-testid="calendario-mes-anterior">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium capitalize w-40 text-center text-slate-800 dark:text-slate-200" data-testid="calendario-mes-label">
              {ref.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}
            </span>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setRef(new Date(y, m + 1, 1))} data-testid="calendario-mes-siguiente">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Cada hallazgo <strong>abierto</strong> aparece en su día de vencimiento (fecha compromiso o vencimiento SLA según prioridad). <span className="text-red-600 font-medium">Rojo</span> = vencido. Haz clic para abrir el hallazgo.
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando hallazgos...</div>
        ) : (
          <div className="border rounded-lg overflow-hidden dark:border-slate-700 bg-white dark:bg-slate-800">
            <div className="grid grid-cols-7 bg-[#0C314C] text-white text-xs font-medium">
              {DIAS.map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 divide-x divide-y divide-slate-100 dark:divide-slate-700">
              {celdas.map((dia, i) => {
                if (dia === null) return <div key={`e${i}`} className="min-h-[92px] bg-slate-50/60 dark:bg-slate-900/40" />;
                const fecha = `${y}-${String(m + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
                const lista = porDia[fecha] || [];
                const esHoy = fecha === hoyStr;
                return (
                  <div key={fecha} className={`min-h-[92px] p-1.5 ${esHoy ? "bg-blue-50 dark:bg-blue-900/20" : ""}`} data-testid={`calendario-dia-${fecha}`}>
                    <div className={`text-xs mb-1 ${esHoy ? "font-bold text-blue-700 dark:text-blue-300" : "text-slate-500"}`}>{dia}</div>
                    <div className="space-y-1">
                      {lista.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => onOpenTicket && onOpenTicket(t.id)}
                          title={`#${t.numero} — ${t.titulo} (${t.asignado_a_nombre || "sin asignar"}) · Vence: ${t._vencimiento}${t._esCompromiso ? " (compromiso)" : " (SLA)"}`}
                          className={`block w-full text-left text-[10px] leading-tight rounded px-1 py-0.5 truncate cursor-pointer transition-colors ${fecha < hoyStr ? "bg-red-100 text-red-700 border border-red-200 hover:bg-red-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"}`}
                          data-testid={`calendario-item-${t.id}`}
                        >
                          #{t.numero} {t.titulo}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
